import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class TabTitleService {
  private readonly overrides = signal<Record<string, string>>({});
  private readonly revision = signal(0);

  readonly version = this.revision.asReadonly();

  constructor(private readonly router: Router) {}

  setCurrent(title: string): void {
    this.setForUrl(this.router.url, title);
  }

  setForUrl(url: string, title: string): void {
    const normalizedUrl = this.normalizeUrl(url);
    if (!normalizedUrl || !title?.trim()) {
      return;
    }

    this.overrides.update(current => ({ ...current, [normalizedUrl]: title.trim() }));
    this.bumpVersion();
  }

  clearCurrent(): void {
    this.clearForUrl(this.router.url);
  }

  clearForUrl(url: string): void {
    const normalizedUrl = this.normalizeUrl(url);
    if (!normalizedUrl) {
      return;
    }

    this.overrides.update(current => {
      if (!(normalizedUrl in current)) {
        return current;
      }

      const { [normalizedUrl]: _, ...rest } = current;
      return rest;
    });
    this.bumpVersion();
  }

  getForUrl(url: string): string | null {
    const normalizedUrl = this.normalizeUrl(url);
    if (!normalizedUrl) {
      return null;
    }

    return this.overrides()[normalizedUrl] ?? null;
  }

  private bumpVersion(): void {
    this.revision.update(value => value + 1);
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return '';
    }

    const path = url.split(/[?#]/)[0] ?? url;
    return path.startsWith('/') ? path : `/${path}`;
  }
}
